import { useEffect, useRef, useState } from 'react'

import BottomSheet from '../../../components/BottomSheet'

/**
 * Stack-edit bottom sheet. Tap-outside or "Avbryt" closes; "Lagre" sends
 * the new value via `currentPlayerPokerStackUpdate`. Number-only input,
 * empty disables submit.
 *
 * Slide animation + Escape-to-close + backdrop tap + body scroll lock
 * all live in `BottomSheet`. This file owns the input + button row.
 */
const StackEditSheet = ({
  open,
  initial,
  onClose,
  onSave,
  saving,
}: {
  open: boolean
  initial: number
  onClose: () => void
  onSave: (next: number) => void
  saving: boolean
}) => {
  const [value, setValue] = useState<string>(String(initial))
  const inputRef = useRef<HTMLInputElement | null>(null)
  useEffect(() => {
    if (open) setValue(String(initial))
  }, [open, initial])
  // Focus the input on open without relying on autoFocus (which lint
  // disallows). Defer to next frame so the dialog is in the DOM after
  // BottomSheet's slide-in starts.
  useEffect(() => {
    if (!open) return undefined
    const id = window.requestAnimationFrame(() => inputRef.current?.focus())
    return () => window.cancelAnimationFrame(id)
  }, [open])

  const parsed = Number.parseInt(value, 10)
  const valid = Number.isFinite(parsed) && parsed >= 0

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      ariaLabel="Oppdater chipstack"
      panelClassName="max-w-md bg-paper p-5 flex flex-col gap-4"
    >
      <div>
        <h2 className="font-display font-bold text-lg text-ink-900">
          Oppdater chipstack
        </h2>
        <p className="text-sm text-ink-500 mt-1">
          Skriv inn antall chips du har akkurat nå.
        </p>
      </div>
      <input
        ref={inputRef}
        type="number"
        inputMode="numeric"
        min={0}
        step={100}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="0"
        className="w-full border border-ink-200 rounded-lg px-4 py-3 font-mono num text-2xl font-bold text-ink-900 focus:outline-none focus:border-green-700 focus:ring-2 focus:ring-green-700/30"
      />
      <div className="flex gap-3">
        <button
          type="button"
          onClick={onClose}
          className="flex-1 px-4 py-3 rounded-lg border border-ink-200 text-ink-700 font-semibold active:opacity-70"
        >
          Avbryt
        </button>
        <button
          type="button"
          disabled={!valid || saving}
          onClick={() => {
            if (valid) onSave(parsed)
          }}
          className="flex-1 px-4 py-3 rounded-lg bg-green-700 text-paper font-semibold active:opacity-70 disabled:opacity-50"
        >
          {saving ? 'Lagrer…' : 'Lagre'}
        </button>
      </div>
    </BottomSheet>
  )
}

export default StackEditSheet
